import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Audiotrack from "@mui/icons-material/Audiotrack";
import Delete from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import VideoLibrary from "@mui/icons-material/VideoLibrary";
import type {
  MediaOverlay,
  MediaOverlayAnimationType,
  MediaOverlayKind,
} from "@ebook/contracts";
import { GlobalFileInput, GlobalInput } from "./GlobalInput";
import { useTranslate } from "../i18n";

type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

const mediaAnimationTypes: MediaOverlayAnimationType[] = [
  "none",
  "fade",
  "fade-up",
  "slide-left",
  "slide-right",
  "zoom-in",
];

export const mediaKindLabels: Record<MediaOverlayKind, string> = {
  youtube: "YouTube",
  audio_url: "Audio URL",
  video_url: "Video URL",
  audio_asset: "Audio",
  video_asset: "Video",
  page_link: "Link",
};

type PageEffectsDialogProps = {
  open: boolean;
  onClose: () => void;
  pageNumber: number | null;
  pageCount: number;
  previewUrl: string | null;
  items: MediaOverlay[];
  selectedItem: MediaOverlay | null;
  selectedItemId: string | null;
  saveStatus: SaveStatus;
  error: string;
  uploading: "audio_asset" | "video_asset" | null;
  revision: number;
  onSelectItem: (id: string) => void;
  onAddVideo: () => void;
  onAddAudio: () => void;
  onAddLink: () => void;
  onUpdateItem: (id: string, update: Partial<MediaOverlay>) => void;
  onUpdateRect: (id: string, rect: MediaOverlay["rect"]) => void;
  onChangeSource: (item: MediaOverlay, kind: MediaOverlayKind) => void;
  onDelete: (id: string) => void;
  onUpload: (kind: "audio_asset" | "video_asset", file: File, replaceId?: string) => void;
  itemsReadyForSave: boolean;
};

type OverlayHandle =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "center"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

const overlayHandles: Array<{
  handle: OverlayHandle;
  label: string;
  sx: Record<string, unknown>;
  cursor: string;
}> = [
  { handle: "top-left", label: "top-left", sx: { left: 0, top: 0, transform: "translate(-50%, -50%)" }, cursor: "nwse-resize" },
  { handle: "top-center", label: "top-center", sx: { left: "50%", top: 0, transform: "translate(-50%, -50%)" }, cursor: "ns-resize" },
  { handle: "top-right", label: "top-right", sx: { right: 0, top: 0, transform: "translate(50%, -50%)" }, cursor: "nesw-resize" },
  { handle: "middle-left", label: "middle-left", sx: { left: 0, top: "50%", transform: "translate(-50%, -50%)" }, cursor: "ew-resize" },
  { handle: "center", label: "move", sx: { left: "50%", top: "50%", transform: "translate(-50%, -50%)" }, cursor: "move" },
  { handle: "middle-right", label: "middle-right", sx: { right: 0, top: "50%", transform: "translate(50%, -50%)" }, cursor: "ew-resize" },
  { handle: "bottom-left", label: "bottom-left", sx: { left: 0, bottom: 0, transform: "translate(-50%, 50%)" }, cursor: "nesw-resize" },
  { handle: "bottom-center", label: "bottom-center", sx: { left: "50%", bottom: 0, transform: "translate(-50%, 50%)" }, cursor: "ns-resize" },
  { handle: "bottom-right", label: "bottom-right", sx: { right: 0, bottom: 0, transform: "translate(50%, 50%)" }, cursor: "nwse-resize" },
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function resizeRect(
  rect: MediaOverlay["rect"],
  handle: OverlayHandle,
  dx: number,
  dy: number,
): MediaOverlay["rect"] {
  const minimum = 5;
  if (handle === "center") {
    return {
      ...rect,
      left: clamp(rect.left + dx, 0, 100 - rect.width),
      top: clamp(rect.top + dy, 0, 100 - rect.height),
    };
  }

  let left = rect.left;
  let top = rect.top;
  let width = rect.width;
  let height = rect.height;
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;

  if (handle.includes("left")) {
    left = clamp(rect.left + dx, 0, right - minimum);
    width = right - left;
  }
  if (handle.includes("right")) {
    width = clamp(rect.width + dx, minimum, 100 - rect.left);
  }
  if (handle.includes("top")) {
    top = clamp(rect.top + dy, 0, bottom - minimum);
    height = bottom - top;
  }
  if (handle.includes("bottom")) {
    height = clamp(rect.height + dy, minimum, 100 - rect.top);
  }

  return { left, top, width, height };
}

export function PageEffectsDialog({
  open,
  onClose,
  pageNumber,
  pageCount,
  previewUrl,
  items,
  selectedItem,
  selectedItemId,
  saveStatus,
  error,
  uploading,
  revision,
  onSelectItem,
  onAddVideo,
  onAddAudio,
  onAddLink,
  onUpdateItem,
  onUpdateRect,
  onChangeSource,
  onDelete,
  onUpload,
  itemsReadyForSave,
}: PageEffectsDialogProps) {
  const __ = useTranslate();
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const isMedia = selectedItem && selectedItem.kind !== "page_link";

  function startOverlayDrag(
    event: ReactPointerEvent<HTMLElement>,
    item: MediaOverlay,
    handle: OverlayHandle,
  ) {
    const frame = previewFrameRef.current;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    onSelectItem(item.id);
    const bounds = frame.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const start = item.rect;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const onMove = (moveEvent: PointerEvent) => {
      const dx = ((moveEvent.clientX - startX) / bounds.width) * 100;
      const dy = ((moveEvent.clientY - startY) / bounds.height) * 100;
      onUpdateRect(item.id, resizeRect(start, handle, dx, dy));
    };
    const onUp = () => {
      target.releasePointerCapture(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") onClose();
      }}
      aria-labelledby="page-effects-dialog-title"
    >
      <DialogTitle id="page-effects-dialog-title">
        {__("เอฟเฟคบนหน้า {{page}}", { page: pageNumber ?? "-" })}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.35fr) minmax(320px, .65fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" fontWeight={700}>
              {__("ตัวอย่างหน้า {{page}}", { page: pageNumber ?? "-" })}
            </Typography>
            {previewUrl ? (
              <Box
                ref={previewFrameRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  maxHeight: { xs: 520, md: "calc(100vh - 260px)" },
                  overflow: "hidden",
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  lineHeight: 0,
                }}
              >
                <Box
                  component="img"
                  src={previewUrl}
                  alt={__("ตัวอย่างหน้า {{page}}", { page: pageNumber ?? "-" })}
                  sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: { xs: 520, md: "calc(100vh - 260px)" },
                    objectFit: "contain",
                  }}
                />
                {items.map((item) => {
                  const active = selectedItemId === item.id;
                  return (
                    <Box
                      key={item.id}
                      onPointerDown={(event) => startOverlayDrag(event, item, "center")}
                      onClick={() => onSelectItem(item.id)}
                      sx={{
                        position: "absolute",
                        left: `${item.rect.left}%`,
                        top: `${item.rect.top}%`,
                        width: `${item.rect.width}%`,
                        height: `${item.rect.height}%`,
                        zIndex: item.zIndex,
                        minHeight: 18,
                        border: 2,
                        borderColor: active ? "primary.main" : "warning.main",
                        bgcolor: item.kind === "page_link"
                          ? "rgba(46,125,50,0.16)"
                          : item.kind.includes("audio")
                            ? "rgba(25,118,210,0.18)"
                            : "rgba(255,152,0,0.18)",
                        cursor: "move",
                        lineHeight: 1.2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: 4,
                          top: 3,
                          px: 0.5,
                          borderRadius: 0.75,
                          bgcolor: "rgba(255,255,255,0.86)",
                          color: "text.primary",
                          fontWeight: 700,
                          pointerEvents: "none",
                        }}
                      >
                        {__(mediaKindLabels[item.kind])}
                      </Typography>
                      {active && overlayHandles.map(({ handle, label, sx, cursor }) => (
                        <Box
                          key={handle}
                          component="button"
                          type="button"
                          aria-label={__("ปรับพื้นที่ {{handle}}", { handle: label })}
                          onPointerDown={(event) => startOverlayDrag(event, item, handle)}
                          sx={{
                            position: "absolute",
                            width: handle === "center" ? 18 : 12,
                            height: handle === "center" ? 18 : 12,
                            p: 0,
                            border: 2,
                            borderColor: "primary.main",
                            borderRadius: handle === "center" ? "50%" : 0.75,
                            bgcolor: handle === "center" ? "primary.main" : "background.paper",
                            cursor,
                            ...sx,
                          }}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {__("ไม่พบภาพตัวอย่างของหน้านี้")}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {__("ลากจุดกลางเพื่อย้ายพื้นที่ หรือใช้จุดรอบกรอบทั้ง 8 ทิศเพื่อปรับขนาด")}
            </Typography>
          </Stack>

          <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<VideoLibrary />} disabled={pageNumber === null} onClick={onAddVideo}>
              {__("Add Video")}
            </Button>
            <Button size="small" variant="outlined" startIcon={<Audiotrack />} disabled={pageNumber === null} onClick={onAddAudio}>
              {__("Add Audio")}
            </Button>
            <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={pageNumber === null} onClick={onAddLink}>
              {__("Add Link")}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="caption" color={saveStatus === "error" ? "error" : "text.secondary"}>
            {{
              idle: __("Effect revision {{revision}}", { revision }),
              pending: itemsReadyForSave
                ? __("รอบันทึกเอฟเฟคอัตโนมัติ…")
                : __("กรอกข้อมูลเอฟเฟคให้ครบก่อนบันทึกอัตโนมัติ"),
              saving: __("กำลังบันทึกเอฟเฟคอัตโนมัติ…"),
              saved: __("บันทึกเอฟเฟคอัตโนมัติแล้ว"),
              error: __("บันทึกเอฟเฟคอัตโนมัติไม่สำเร็จ"),
            }[saveStatus]}
          </Typography>

          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {__("ยังไม่มีเอฟเฟคในหน้านี้")}
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {items.map((item) => (
                <Button
                  key={item.id}
                  size="small"
                  variant={selectedItemId === item.id ? "contained" : "outlined"}
                  onClick={() => onSelectItem(item.id)}
                  sx={{ justifyContent: "space-between" }}
                >
                  <span>{__(mediaKindLabels[item.kind])}</span>
                  <span>z {item.zIndex}</span>
                </Button>
              ))}
            </Stack>
          )}

          {selectedItem && (
            <Stack spacing={1.25}>
              {isMedia && (
                <GlobalInput
                  select
                  label={__("Source")}
                  value={selectedItem.kind}
                  onChange={(event) => onChangeSource(selectedItem, event.target.value as MediaOverlayKind)}
                >
                  {selectedItem.kind.startsWith("video") || selectedItem.kind === "youtube"
                    ? [
                        <MenuItem key="youtube" value="youtube">YouTube</MenuItem>,
                        <MenuItem key="video_url" value="video_url">{__("Video URL")}</MenuItem>,
                        <MenuItem key="video_asset" value="video_asset">{__("File")}</MenuItem>,
                      ]
                    : [
                        <MenuItem key="audio_url" value="audio_url">{__("Audio URL")}</MenuItem>,
                        <MenuItem key="audio_asset" value="audio_asset">{__("File")}</MenuItem>,
                      ]}
                </GlobalInput>
              )}

              {(selectedItem.kind === "youtube"
                || selectedItem.kind === "audio_url"
                || selectedItem.kind === "video_url") && (
                <GlobalInput
                  fullWidth
                  label={__("URL")}
                  value={selectedItem.sourceUrl ?? ""}
                  onChange={(event) => onUpdateItem(selectedItem.id, { sourceUrl: event.target.value })}
                />
              )}

              {(selectedItem.kind === "audio_asset" || selectedItem.kind === "video_asset") && (
                <Stack spacing={0.75}>
                  {selectedItem.assetKey && (
                    <Typography variant="caption" color="text.secondary">
                      {__("File uploaded: {{name}}", { name: selectedItem.assetKey.split("/").at(-1) ?? selectedItem.assetKey })}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading !== null}
                    startIcon={selectedItem.kind === "audio_asset" ? <Audiotrack /> : <VideoLibrary />}
                  >
                    {uploading === selectedItem.kind
                      ? __("Uploading…")
                      : selectedItem.assetKey
                        ? __("Replace File")
                        : __("Upload File")}
                    <GlobalFileInput
                      accept={selectedItem.kind === "audio_asset"
                        ? "audio/mpeg,audio/mp4,audio/wav,audio/ogg"
                        : "video/mp4,video/webm,video/ogg"}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = "";
                        if (file) {
                          const uploadKind = selectedItem.kind === "audio_asset" ? "audio_asset" : "video_asset";
                          onUpload(uploadKind, file, selectedItem.id);
                        }
                      }}
                    />
                  </Button>
                </Stack>
              )}

              {selectedItem.kind === "page_link" && (
                <GlobalInput
                  type="number"
                  label={__("ไปยังหน้า")}
                  value={selectedItem.targetPageNumber ?? ""}
                  error={
                    selectedItem.targetPageNumber === null
                    || selectedItem.targetPageNumber < 1
                    || selectedItem.targetPageNumber > pageCount
                  }
                  helperText={__("ใส่เลขหน้า 1-{{pageCount}}", { pageCount })}
                  onChange={(event) => onUpdateItem(selectedItem.id, {
                    targetPageNumber: Number.parseInt(event.target.value, 10) || null,
                  })}
                  inputProps={{ min: 1, max: pageCount }}
                />
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <GlobalInput type="number" label={__("Left %")} value={selectedItem.rect.left} onChange={(event) => onUpdateRect(selectedItem.id, { ...selectedItem.rect, left: Number(event.target.value) })} />
                <GlobalInput type="number" label={__("Top %")} value={selectedItem.rect.top} onChange={(event) => onUpdateRect(selectedItem.id, { ...selectedItem.rect, top: Number(event.target.value) })} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <GlobalInput type="number" label={__("Width %")} value={selectedItem.rect.width} onChange={(event) => onUpdateRect(selectedItem.id, { ...selectedItem.rect, width: Number(event.target.value) })} />
                <GlobalInput type="number" label={__("Height %")} value={selectedItem.rect.height} onChange={(event) => onUpdateRect(selectedItem.id, { ...selectedItem.rect, height: Number(event.target.value) })} />
              </Stack>
              <GlobalInput
                type="number"
                label={__("z-index")}
                value={selectedItem.zIndex}
                onChange={(event) => onUpdateItem(selectedItem.id, { zIndex: Number(event.target.value) })}
              />

              {isMedia && (
                <>
                  <GlobalInput
                    select
                    label={__("Animation")}
                    value={selectedItem.animationType}
                    onChange={(event) => onUpdateItem(selectedItem.id, { animationType: event.target.value as MediaOverlayAnimationType })}
                  >
                    {mediaAnimationTypes.map((type) => (
                      <MenuItem key={type} value={type}>{__(type)}</MenuItem>
                    ))}
                  </GlobalInput>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <GlobalInput type="number" label={__("Delay ms")} value={selectedItem.animationDelayMs} onChange={(event) => onUpdateItem(selectedItem.id, { animationDelayMs: Number(event.target.value) })} />
                    <GlobalInput type="number" label={__("Duration ms")} value={selectedItem.animationDurationMs} onChange={(event) => onUpdateItem(selectedItem.id, { animationDurationMs: Number(event.target.value) })} />
                  </Stack>
                </>
              )}

              <FormControlLabel
                control={<Switch checked={selectedItem.enabled} onChange={(event) => onUpdateItem(selectedItem.id, { enabled: event.target.checked })} />}
                label={__("เปิดใช้งาน")}
              />

              <Box>
                <Button color="error" startIcon={<Delete />} onClick={() => onDelete(selectedItem.id)}>
                  {__("ลบ")}
                </Button>
                {saveStatus === "saving" && <CircularProgress size={24} sx={{ ml: 1 }} />}
              </Box>
            </Stack>
          )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{__("ปิด")}</Button>
      </DialogActions>
    </Dialog>
  );
}
